import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Home, PenTool, BarChart3, Settings, LogOut, Moon, Sun, Workflow } from 'lucide-react';

function UserAvatar({ name }) {
  const initials = name
    ? name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';
  return (
    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0">
      {initials}
    </div>
  );
}

const MAIN_NAV = [
  { to: '/dashboard', icon: Home, label: 'Home' },
  { to: '/workspace', icon: PenTool, label: 'Workspace' },
  { to: '/record', icon: BarChart3, label: 'Record' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [theme, setTheme] = React.useState(localStorage.getItem('theme') || 'light');

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  React.useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors font-['IBM_Plex_Sans'] text-sm ${
      isActive
        ? 'bg-accent text-foreground font-semibold'
        : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground'
    }`;

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-64 border-r border-border bg-card flex flex-col shrink-0">
        {/* Header */}
        <div className="p-5 border-b border-border">
          <h1 className="text-lg font-bold tracking-tight leading-tight">The Journalist's Lab</h1>
          <div className="flex items-center gap-2 mt-2">
            <UserAvatar name={user?.name} />
            <p className="text-sm text-muted-foreground font-['IBM_Plex_Sans'] truncate">{user?.name}</p>
          </div>
        </div>

        {/* Main nav */}
        <nav className="flex-1 p-3 space-y-0.5">
          {MAIN_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={navLinkClass}
            >
              <item.icon className="w-4 h-4 shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          ))}

          {/* Settings + BA separator */}
          <div className="pt-3 mt-2 border-t border-border space-y-0.5">
            <NavLink
              to="/settings"
              data-testid="nav-settings"
              className={navLinkClass}
            >
              <Settings className="w-4 h-4 shrink-0" />
              <span>Settings</span>
            </NavLink>
            <NavLink
              to="/business-analysis"
              data-testid="nav-studio"
              className={navLinkClass}
            >
              <Workflow className="w-4 h-4 shrink-0" />
              <span>Business Analysis</span>
            </NavLink>
          </div>
        </nav>

        {/* Footer actions */}
        <div className="p-3 border-t border-border space-y-1">
          <Button
            data-testid="btn-theme-toggle"
            variant="ghost"
            onClick={toggleTheme}
            className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground text-sm"
          >
            {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
          </Button>
          <Button
            data-testid="btn-logout"
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground text-sm"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
